import { useState, useMemo } from "react";
import {
  Loader2,
  CreditCard,
  Ticket,
  ChevronDown,
  ChevronRight,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { EnrollmentBillingItem } from "../../api/getEnrollmentBilling/types";
import { EnrollmentBillingDrawer } from "./EnrollmentBillingDrawer";
import { coverageBadge, stateBadge } from "./badges";
import { SendPaymentRequestModal } from "@/modules/eventRegistration/features/PaymentRequests/components/SendPaymentRequestModal/SendPaymentRequestModal";
import { resendPaymentRequest } from "@/modules/eventRegistration/features/PaymentRequests/api/resendPaymentRequest";
import { PaymentRequestRecipient } from "@/modules/eventRegistration/features/PaymentRequests/types/paymentRequest";

interface TransactionsViewProps {
  enrollments: EnrollmentBillingItem[];
  isLoading: boolean;
  onRefundSuccess: () => void;
}

function fmt(amount: string | number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(typeof amount === "string" ? parseFloat(amount) : amount);
}

// An unpaid cash seat — no payment captured, not refunded, not
// cancelled. The "promoted off the waitlist, no card on file" case the
// payment link is built for.
function isUnpaidCashSeat(item: EnrollmentBillingItem): boolean {
  return (
    item.coverageType === "cash" &&
    !item.isPaid &&
    !item.isRefunded &&
    item.lifecycleState !== "cancelled"
  );
}

// A row the admin can send a payment link for — unpaid cash with no
// pending request already covering it. Exported so the per-session
// Payment Requests tab's "Generate" checklist uses the exact same
// definition of "billable" as the billing rows.
export function isSendable(item: EnrollmentBillingItem): boolean {
  return isUnpaidCashSeat(item) && item.pendingPaymentRequest === null;
}

// --- Financial summary ---

function FinancialSummary({
  enrollments,
}: {
  enrollments: EnrollmentBillingItem[];
}) {
  const totals = useMemo(() => {
    let revenue = 0;
    let paidNet = 0;
    let outstanding = 0;
    let refunded = 0;
    let vouchers = 0;

    enrollments.forEach((item) => {
      const net = parseFloat(item.netAmount) || 0;

      if (item.coverageType === "voucher") {
        vouchers += parseFloat(item.seatPrice) || 0;
      } else if (item.coverageType === "cash") {
        revenue += net;
        // "Collected" must reflect money that actually changed hands —
        // a cash seat with no payment captured (e.g. awaiting a payment
        // link) is owed, not collected.
        if (item.isPaid) {
          paidNet += net;
        } else if (item.lifecycleState !== "cancelled") {
          outstanding += net;
        }
        if (item.isRefunded) {
          refunded += parseFloat(item.refundedAmount) || 0;
        }
      }
    });

    return {
      revenue,
      refunded,
      vouchers,
      outstanding,
      collected: paidNet - refunded,
    };
  }, [enrollments]);

  const stats = [
    { label: "Revenue", value: fmt(totals.revenue) },
    { label: "Collected", value: fmt(totals.collected) },
    { label: "Outstanding", value: fmt(totals.outstanding) },
    { label: "Refunded", value: fmt(totals.refunded) },
    { label: "Vouchers", value: fmt(totals.vouchers) },
  ];

  return (
    <div className="flex items-center gap-6 mb-5">
      {stats.map((stat) => (
        <div className="flex items-center gap-2" key={stat.label}>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {stat.label}
          </span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {stat.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// --- Company group ---

interface CompanyBillingGroup {
  companyName: string;
  items: EnrollmentBillingItem[];
  totalAmount: number;
  totalRefunded: number;
  totalVouchers: number;
}

function groupBillingByCompany(items: EnrollmentBillingItem[]): {
  groups: CompanyBillingGroup[];
  ungrouped: EnrollmentBillingItem[];
} {
  const map = new Map<string, EnrollmentBillingItem[]>();
  const ungrouped: EnrollmentBillingItem[] = [];

  items.forEach((item) => {
    const company = item.companyName?.trim();
    if (!company) {
      ungrouped.push(item);
      return;
    }
    if (!map.has(company)) {
      map.set(company, []);
    }
    map.get(company)!.push(item);
  });

  const groups: CompanyBillingGroup[] = [];
  map.forEach((companyItems, companyName) => {
    let totalAmount = 0;
    let totalRefunded = 0;
    let totalVouchers = 0;

    companyItems.forEach((item) => {
      const net = parseFloat(item.netAmount) || 0;

      if (item.coverageType === "voucher") {
        totalVouchers += parseFloat(item.seatPrice) || 0;
      } else if (item.coverageType === "cash") {
        totalAmount += net;
        if (item.isRefunded) {
          totalRefunded += parseFloat(item.refundedAmount) || 0;
        }
      }
    });

    groups.push({
      companyName,
      items: companyItems,
      totalAmount,
      totalRefunded,
      totalVouchers,
    });
  });

  groups.sort((a, b) => a.companyName.localeCompare(b.companyName));
  return { groups, ungrouped };
}

// --- Billing row ---

function BillingRow({
  item,
  onSelect,
  onSendPaymentLink,
  onResend,
  onCopyLink,
  resending,
}: {
  item: EnrollmentBillingItem;
  onSelect: (item: EnrollmentBillingItem) => void;
  onSendPaymentLink: (item: EnrollmentBillingItem) => void;
  onResend: (uuid: string) => void;
  onCopyLink: (token: string) => void;
  resending: boolean;
}) {
  const coverage = coverageBadge[item.coverageType];
  const state = stateBadge(item);
  const isCash = item.coverageType === "cash";
  const isVoucher = item.coverageType === "voucher";
  const isTotalAccess = item.coverageType === "total_access";
  const isStageCovered = item.coverageType === "stage";
  const refundedAmount = parseFloat(item.refundedAmount) || 0;
  const isPartiallyRefunded = refundedAmount > 0 && !item.isRefunded;
  const canRefund =
    isCash &&
    parseFloat(item.remainingRefundableAmount) > 0 &&
    !item.isRefunded;
  const canReturnVoucher = isVoucher && item.voucherStatus === "applied";
  const unpaid = isUnpaidCashSeat(item);
  const pending = item.pendingPaymentRequest;

  return (
    <div
      className="flex items-center justify-between py-2.5 px-3 border-b border-gray-100 dark:border-slate-700/50 last:border-b-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
      onClick={() => onSelect(item)}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {item.firstName} {item.lastName}
            </p>
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${coverage.style}`}
            >
              {coverage.label}
            </span>
            {state && (
              <span
                className={`text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${state.style}`}
              >
                {state.label}
              </span>
            )}
            {unpaid && !pending && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                Unpaid
              </span>
            )}
            {pending && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                Payment link sent
                {pending.sentCount > 1 ? ` ${pending.sentCount}×` : ""}
              </span>
            )}
          </div>
          {item.enrolledAt && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              {new Date(item.enrolledAt).toLocaleDateString()}
            </p>
          )}
          <div className="flex items-center gap-2 mt-0.5">
            {isTotalAccess ? (
              <span className="text-xs text-blue-600 dark:text-blue-400">
                Covered by Total Access subscription
              </span>
            ) : isStageCovered ? (
              <span className="text-xs text-emerald-600 dark:text-emerald-400">
                Covered by Stage Growth membership
              </span>
            ) : isCash ? (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <CreditCard className="h-3 w-3" />
                Card ••{item.cardLast4 || "****"}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <Ticket className="h-3 w-3" />
                {item.voucherName || "Voucher"}
              </span>
            )}
            {item.discounts.length > 0 && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {item.discounts[0].code && (
                    <span className="font-mono">{item.discounts[0].code} </span>
                  )}
                  -{fmt(item.totalDiscountAmount)}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0 ml-3">
        <div className="flex flex-col items-end">
          <span
            className={`text-sm font-medium ${
              item.isRefunded
                ? "line-through text-gray-400 dark:text-gray-500"
                : "text-gray-900 dark:text-white"
            }`}
          >
            {fmt(item.seatPrice)}
          </span>
          {isPartiallyRefunded && (
            <span className="text-[10px] text-red-600 dark:text-red-400 mt-0.5 whitespace-nowrap">
              −{fmt(item.refundedAmount)} refunded
            </span>
          )}
        </div>
        {canRefund && (
          <Button
            className="h-7 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(item);
            }}
            size="sm"
            variant="outline"
          >
            Refund
          </Button>
        )}
        {canReturnVoucher && (
          <Button
            className="h-7 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(item);
            }}
            size="sm"
            variant="outline"
          >
            Return
          </Button>
        )}
        {pending && (
          <>
            <Button
              className="h-7 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onCopyLink(pending.publicToken);
              }}
              size="sm"
              variant="ghost"
            >
              Copy payment link
            </Button>
            <Button
              className="h-7 text-xs"
              disabled={resending}
              onClick={(e) => {
                e.stopPropagation();
                onResend(pending.uuid);
              }}
              size="sm"
              variant="outline"
            >
              Resend payment link
            </Button>
          </>
        )}
        {isSendable(item) && (
          <Button
            className="h-7 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onSendPaymentLink(item);
            }}
            size="sm"
            variant="outline"
          >
            Send payment link
          </Button>
        )}
      </div>
    </div>
  );
}

// --- Company billing header ---

function CompanyBillingHeader({
  group,
  isCollapsed,
  onToggle,
}: {
  group: CompanyBillingGroup;
  isCollapsed: boolean;
  onToggle: () => void;
}) {
  const summaryParts: string[] = [];
  if (group.totalAmount > 0) summaryParts.push(fmt(group.totalAmount));
  if (group.totalRefunded > 0)
    summaryParts.push(`${fmt(group.totalRefunded)} refunded`);
  if (group.totalVouchers > 0)
    summaryParts.push(`${fmt(group.totalVouchers)} voucher`);

  return (
    <button
      className="flex items-center gap-1.5 py-2 px-1 w-full text-left"
      onClick={onToggle}
      type="button"
    >
      {isCollapsed ? (
        <ChevronRight className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      ) : (
        <ChevronDown className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      )}
      <Building2 className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {group.companyName}
      </span>
      <span className="text-xs text-gray-400 dark:text-gray-500">
        ({group.items.length})
      </span>
      {summaryParts.length > 0 && (
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
          — {summaryParts.join(" · ")}
        </span>
      )}
    </button>
  );
}

// --- Main component ---

export function TransactionsView({
  enrollments,
  isLoading,
  onRefundSuccess,
}: TransactionsViewProps) {
  const { toast } = useToast();
  const [selectedEnrollment, setSelectedEnrollment] =
    useState<EnrollmentBillingItem | null>(null);
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(
    new Set(),
  );
  const [modalItem, setModalItem] = useState<EnrollmentBillingItem | null>(
    null,
  );
  const [resendingUuid, setResendingUuid] = useState<string | null>(null);

  const { groups, ungrouped } = useMemo(
    () => groupBillingByCompany(enrollments),
    [enrollments],
  );

  const toggleCompany = (key: string) => {
    setCollapsedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleResend = async (uuid: string) => {
    setResendingUuid(uuid);
    try {
      await resendPaymentRequest(uuid);
      toast({ title: "Payment link resent" });
      onRefundSuccess();
    } finally {
      setResendingUuid(null);
    }
  };

  const handleCopyLink = async (token: string) => {
    await navigator.clipboard.writeText(
      `${window.location.origin}/pay/${token}`,
    );
    toast({ title: "Payment link copied" });
  };

  // Single-enrollment send — prefill the modal recipient from the
  // enrollment's checkout contact.
  const defaultRecipients: PaymentRequestRecipient[] = useMemo(() => {
    if (!modalItem) return [];
    const email = modalItem.contactEmail?.trim();
    if (!email) return [];
    return [{ email, name: modalItem.contactName?.trim() || "" }];
  }, [modalItem]);

  const renderRow = (item: EnrollmentBillingItem) => (
    <BillingRow
      item={item}
      key={item.enrollmentId}
      onCopyLink={handleCopyLink}
      onResend={handleResend}
      onSelect={setSelectedEnrollment}
      onSendPaymentLink={setModalItem}
      resending={resendingUuid === item.pendingPaymentRequest?.uuid}
    />
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (enrollments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">
          No billing records found for this session.
        </p>
      </div>
    );
  }

  return (
    <>
      <FinancialSummary enrollments={enrollments} />

      <div className="space-y-1">
        {groups.map((group) => {
          const key = `billing:${group.companyName}`;
          const isCollapsed = collapsedCompanies.has(key);

          return (
            <div key={group.companyName}>
              <CompanyBillingHeader
                group={group}
                isCollapsed={isCollapsed}
                onToggle={() => toggleCompany(key)}
              />
              {!isCollapsed && (
                <div className="ml-5 border border-gray-100 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800">
                  {group.items.map(renderRow)}
                </div>
              )}
            </div>
          );
        })}

        {ungrouped.length > 0 && (
          <div className="border border-gray-100 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800">
            {ungrouped.map(renderRow)}
          </div>
        )}
      </div>

      <EnrollmentBillingDrawer
        enrollment={selectedEnrollment}
        onClose={() => setSelectedEnrollment(null)}
        onRefundSuccess={() => {
          setSelectedEnrollment(null);
          onRefundSuccess();
        }}
        open={!!selectedEnrollment}
      />

      <SendPaymentRequestModal
        defaultRecipients={defaultRecipients}
        enrollments={
          modalItem
            ? [
                {
                  enrollmentId: modalItem.enrollmentId,
                  attendeeName:
                    `${modalItem.firstName} ${modalItem.lastName}`.trim(),
                  amount: modalItem.seatPrice,
                },
              ]
            : []
        }
        onClose={() => setModalItem(null)}
        onSent={() => {
          setModalItem(null);
          onRefundSuccess();
        }}
        open={!!modalItem}
      />
    </>
  );
}
